import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

import { formatDate, getClassNames, retrieveFromLocalStorage, isURL } from '../../../utils/util';

import Image from '../image/Image';
import Thumbnails from '../thumbnails/Thumbnails';
import CommentInputContainer from '../../../containers/common/comment/comment-input/CommentInput.container';
import CommentContainer from '../../../containers/common/comment/Comment.container';
import PollContainer from '../../../containers/common/poll/Poll.container';
import ListingCard from '../listing/listing-card/ListingCard';

import { ReactComponent as ProfileIcon } from '../../../resources/icons/profile.svg';
import { ReactComponent as ThreeDotsIcon } from '../../../resources/icons/three-dots.svg';
import { ReactComponent as DirectMessageIcon } from '../../../resources/icons/direct-message.svg';
import { ReactComponent as EditIcon } from '../../../resources/icons/buzz-edit.svg';
import { ReactComponent as TrashIcon } from '../../../resources/icons/buzz-trash.svg';
import { ReactComponent as LikeIcon } from '../../../resources/icons/like.svg';
import { ReactComponent as LikeFilledIcon } from '../../../resources/icons/like-filled.svg';

import './post.scss';

const Post = ({
  post,
  reactions,
  showAllComments,
  adminRole,
  createReaction,
  deleteReaction,
  deletePost,
  retrieveChat,
  viewAllComments,
  navigateToEditPost,
  bookmarkListing,
  navigateToProduct,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(0);

  const reactionRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const reaction = reactions?.find(
      (reaction) => reaction.parentPostExternalReference === post.externalReference && reaction.reaction === 'like'
    );
    reactionRef.current = reaction;
    setLiked(!!reaction);
  }, [reactions, post.externalReference]);

  useEffect(() => {
    const likes = post.reactionCount.find((reaction) => reaction.text === 'like')?.count || 0;
    setLikes(likes);
  }, [post.reactionCount]);

  const personExternalReference = retrieveFromLocalStorage('personExternalReference');
  const isPostCreator = post.personExternalReference === personExternalReference;

  const handleOutsideClick = (event) => {
    if (menuRef.current && !menuRef.current.contains(event.target)) {
      setShowMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleMenuMouseDown = (event) => {
    if (showMenu) {
      event.stopPropagation();
    }
  };

  const handleMenuClick = () => setShowMenu(true);

  const handleDirectMessageClick = () => {
    retrieveChat(post.personExternalReference);
    setShowMenu(false);
  };

  const handleEditPostClick = () => navigateToEditPost();

  const handleRemovePostClick = () => deletePost(post.externalReference);

  const handleLikeClick = () => {
    if (liked) {
      setLiked(false);
      setLikes(likes - 1);
      deleteReaction(reactionRef.current, () => {
        setLiked(true);
        setLikes(likes);
      });
    } else {
      setLiked(true);
      setLikes(likes + 1);
      createReaction('like', () => {
        setLiked(false);
        setLikes(likes);
      });
    }
  };

  const renderMenu = () => {
    return (
      <article className="post__menu" ref={menuRef}>
        {!isPostCreator && post.canMessage && (
          <button className="post__menu__button" onClick={handleDirectMessageClick}>
            <DirectMessageIcon className="post__menu__icon" />
            Direct Message
          </button>
        )}
        {isPostCreator && (
          <button className="post__menu__button" onClick={handleEditPostClick}>
            <EditIcon className="post__menu__icon" />
            Edit Post
          </button>
        )}
        {(isPostCreator || adminRole) && (
          <button className="post__menu__button" onClick={handleRemovePostClick}>
            <TrashIcon className="post__menu__icon" />
            Remove Post
          </button>
        )}
      </article>
    );
  };

  const renderComments = () => {
    return post.comments.map((comment) => (
      <CommentContainer
        key={comment.externalReference}
        postExternalReference={post.externalReference}
        parentExternalReference={post.externalReference}
        comment={comment}
        reactions={reactions}
        adminRole={adminRole}
        indent={0}
        showAllComments={showAllComments}
      />
    ));
  };

  const renderText = () => {
    if (!post.text) {
      return null;
    }

    const paragraphs = post.text.trim().split('\n');
    return paragraphs.map((paragraph, index) => {
      if (paragraph === '') {
        return <br key={index} />;
      }

      const splitText = paragraph.split(' ');
      const textWithLinks = splitText.map((text, index) => {
        if (isURL(text)) {
          return (
            <Link key={index} className="post__text post__text--link" target="_blank" to={text}>
              {text}
            </Link>
          );
        }

        if (index === splitText.length - 1) {
          return text;
        } else {
          return text + ' ';
        }
      });

      return (
        <p key={index} className="post__text">
          {textWithLinks}
        </p>
      );
    });
  };

  return (
    <section className="post">
      <section className="post__header">
        <div className="post__profile-picture">
          {post.personProfilePictureExternalReference ? (
            <Image
              className="post__profile-picture__image"
              key={post.personProfilePictureExternalReference}
              externalReference={post.personProfilePictureExternalReference}
              alt=""
            />
          ) : (
            <ProfileIcon className="post__profile-picture__icon" />
          )}
        </div>
        <div className="post__header__text">
          <p className="post__header__username">{post.userName}</p>
          <p className="post__header__date">{formatDate(post.dateTime)}</p>
        </div>
        {((post.canMessage && !isPostCreator) || isPostCreator || adminRole) && (
          <ThreeDotsIcon
            className={getClassNames('post__header__three-dots-icon', { active: showMenu })}
            onMouseDown={handleMenuMouseDown}
            onClick={handleMenuClick}
          />
        )}
        {showMenu && renderMenu()}
      </section>

      {post.productExternalReference && (
        <ListingCard
          title={post.productName}
          description={post.productPrice}
          images={[post.productPictureExternalReference]}
          bookmarkListing={bookmarkListing}
          navigateToProduct={navigateToProduct}
          post
        />
      )}

      <section className="post__text-container">{renderText()}</section>

      <Thumbnails images={post.linkedMediaReferences} />

      {post.question && (
        <PollContainer
          postExternalReference={post.externalReference}
          question={post.question}
          options={post.options}
          reactions={reactions}
          reactionCount={post.reactionCount}
        />
      )}

      <section className="post__reactions">
        <button className="post__reactions__likes" onClick={handleLikeClick}>
          {liked ? (
            <LikeFilledIcon className="post__reactions__likes__icon" />
          ) : (
            <LikeIcon className="post__reactions__likes__icon" />
          )}
          {likes > 0 && <p className="post__reactions__likes__number">{likes}</p>}
        </button>
        <div className="post__reactions__comments">
          <p>
            {post.totalComments} {post.totalComments === 1 ? 'Comment' : 'Comments'}
          </p>
        </div>
      </section>

      <CommentInputContainer parentExternalReference={post.externalReference} />

      {post.comments && post.comments.length > 0 && (
        <section className="post__comments">
          {renderComments()}
          {post.totalComments > 1 && !showAllComments && (
            <button className="post__comments__view-all" onClick={viewAllComments}>
              View All Comments
            </button>
          )}
        </section>
      )}
    </section>
  );
};

Post.propTypes = {
  post: PropTypes.shape({}).isRequired,
  reactions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showAllComments: PropTypes.bool.isRequired,
  adminRole: PropTypes.bool,
  createReaction: PropTypes.func.isRequired,
  deleteReaction: PropTypes.func.isRequired,
  deletePost: PropTypes.func.isRequired,
  retrieveChat: PropTypes.func.isRequired,
  viewAllComments: PropTypes.func.isRequired,
  navigateToEditPost: PropTypes.func.isRequired,
  bookmarkListing: PropTypes.func,
  navigateToProduct: PropTypes.func.isRequired,
};

Post.defaultProps = {
  adminRole: false,
  bookmarkListing: undefined,
};

export default Post;
